import { Injectable } from '@angular/core';
import { Firestore, collection, collectionData, query, orderBy, updateDoc, doc, getDocs, Timestamp, addDoc, deleteDoc } from '@angular/fire/firestore';
import { Observable, from, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { NotificationRequest } from '../../models/notification-request';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  constructor(private firestore: Firestore) {}

  // Get all notifications for a user, sorted by timestamp
  getNotificationsByUserUid(uid: string): Observable<any[]> {
    const notifRef = collection(this.firestore, `users/${uid}/notifications`);
    const sortedQuery = query(notifRef, orderBy('timestamp', 'desc'));
    return collectionData(sortedQuery, { idField: 'id' });
  }

  // Get the count of new notifications (where isNew is true)
  getNewNotificationCount(uid: string): Observable<number> {
    return this.getNotificationsByUserUid(uid).pipe(
      map(notifications => notifications.filter(notif => notif.isNew === true).length)
    );
  }

  // Update all notifications to isNew: false for a given user uid
  updateNotificationsToNotNew(uid: string): Observable<void> {
    const notifRef = collection(this.firestore, `users/${uid}/notifications`);
    return from(getDocs(query(notifRef, orderBy('timestamp', 'desc')))).pipe(
      switchMap(snapshot => {
        const newNotifications = snapshot.docs
          .filter(doc => doc.data()['isNew'] === true)
          .map(doc => {
            const notifRef = doc.ref;
            return updateDoc(notifRef, { isNew: false,status:'READ' });
          });
        return from(Promise.all(newNotifications));
      }),
      map(() => void 0)
    );
  }

  
saveNotification(request: NotificationRequest): Observable<void> {
  const notification = {
    title: request.title,
    body: request.body,
    type: request.type,
    status: request.status,
    isNew: true,
    timestamp: request.timestamp || Timestamp.now()
  };

  // Create one Firestore write for each user
  const tasks = request.userIds.map(userId => {
    const notificationRef = collection(this.firestore, 'users', userId, 'notifications');
    return addDoc(notificationRef, notification);
  });

  // Return observable that completes when all writes are done
  return from(Promise.all(tasks).then(() => undefined));
}

deleteNotification(uid: string, notificationId: string): Observable<void> {
    const notifDoc = doc(this.firestore, `users/${uid}/notifications/${notificationId}`);
    return from(deleteDoc(notifDoc)).pipe(map(() => void 0));
  }

  // ✅ Mark a single notification as READ
  markNotificationAsRead(uid: string, notificationId: string): Observable<void> {
    const notifDoc = doc(this.firestore, `users/${uid}/notifications/${notificationId}`);
    return from(updateDoc(notifDoc, { status: 'READ', isNew: false })).pipe(map(() => void 0));
  }
}